import React, { useMemo, useState } from 'react';
import { FaServer, FaNetworkWired, FaChevronDown, FaChevronUp, FaWindows, FaLinux, FaUbuntu, FaRedhat, FaCentos, FaApple, FaFreebsd, FaCircle } from 'react-icons/fa';
import { SiDebian, SiRockylinux } from 'react-icons/si';
import './ServersHierarchyView.css';

// Componente para barra de progreso de métricas
const MetricBar = ({ label, value, color = '#3b82f6', showValue = true }) => {
  const percentage = value != null ? Math.min(Math.max(value, 0), 100) : 0;
  const isEmpty = value == null;

  // Colores según el porcentaje de uso
  const getBarColor = () => {
    if (isEmpty) return '#e5e7eb';
    if (percentage < 60) return '#10b981'; // Verde
    if (percentage < 80) return '#f59e0b'; // Naranja
    return '#ef4444'; // Rojo
  };

  return (
    <div className="metric-bar-container">
      <div className="metric-bar-header">
        <span className="metric-label">{label}</span>
        {showValue && (
          <span className="metric-value">
            {isEmpty ? 'N/A' : `${percentage.toFixed(0)}%`}
          </span>
        )}
      </div>
      <div className="metric-bar-track">
        <div
          className="metric-bar-fill"
          style={{
            width: `${isEmpty ? 0 : percentage}%`,
            backgroundColor: getBarColor()
          }}
        />
      </div>
    </div>
  );
};

// Función para obtener el ícono según el sistema operativo
const getOSIcon = (os) => {
  if (!os) return FaNetworkWired;

  const osLower = os.toLowerCase();

  // Windows
  if (osLower.includes('windows')) return FaWindows;

  // Ubuntu
  if (osLower.includes('ubuntu')) return FaUbuntu;

  // Debian
  if (osLower.includes('debian')) return SiDebian;

  // Red Hat / RHEL
  if (osLower.includes('red hat') || osLower.includes('rhel')) return FaRedhat;

  // CentOS
  if (osLower.includes('centos')) return FaCentos;

  // Rocky Linux
  if (osLower.includes('rocky')) return SiRockylinux;

  // AlmaLinux
  if (osLower.includes('alma')) return FaLinux;

  // macOS
  if (osLower.includes('macos') || osLower.includes('mac os')) return FaApple;

  // FreeBSD
  if (osLower.includes('freebsd')) return FaFreebsd;

  // Linux genérico
  if (osLower.includes('linux')) return FaLinux;

  // Por defecto
  return FaNetworkWired;
};

// Función para obtener el color según el sistema operativo
const getOSColor = (os) => {
  if (!os) return '#6b7280';

  const osLower = os.toLowerCase();

  if (osLower.includes('windows')) return '#00adef';
  if (osLower.includes('ubuntu')) return '#e95420';
  if (osLower.includes('debian')) return '#a80030';
  if (osLower.includes('red hat') || osLower.includes('rhel')) return '#ee0000';
  if (osLower.includes('centos')) return '#932279';
  if (osLower.includes('rocky')) return '#10b981';
  if (osLower.includes('alma')) return '#0eb8e0';
  if (osLower.includes('macos') || osLower.includes('mac os')) return '#000000';
  if (osLower.includes('freebsd')) return '#ab2b28';
  if (osLower.includes('linux')) return '#fcc624';

  return '#6b7280';
};

const RackNode = ({ rack }) => {
  const totalVMs = rack.hosts?.reduce((sum, host) => sum + (host.vms?.length || 0), 0) || 0;

  return (
    <div className="node-rack-compact">
      <div className="rack-icon-compact">
        <FaServer size={24} />
      </div>
      <div className="rack-info-compact">
        <span className="rack-title-compact">{rack.nombre}</span>
        <span className="rack-divider">|</span>
        <span className="rack-location-compact">{rack.ubicacion || 'Sin ubicación'}</span>
        <span className="rack-divider">|</span>
        <span className="rack-stat-compact">{rack.hosts?.length || 0} Hosts</span>
        <span className="rack-divider">•</span>
        <span className="rack-stat-compact">{totalVMs} VMs</span>
        <span className="rack-divider">•</span>
        <span className="rack-stat-compact">{rack.unidades || 42}U</span>
      </div>
    </div>
  );
};

const HostNode = ({ host, isExpanded, onToggle }) => {
  const OSIcon = getOSIcon(host.sistema_operativo);
  const osColor = getOSColor(host.sistema_operativo);
  const hasMetrics = host.cpu_usage != null || host.ram_usage != null || host.disk_usage != null;

  return (
    <div className="node-host-compact" onClick={onToggle}>
      {/* Header con ícono y nombre */}
      <div className="host-main-info">
        <div className="host-icon-container" style={{ color: osColor }}>
          <OSIcon size={24} />
        </div>
        <div className="host-primary">
          <div className="host-title-row">
            <h4 className="host-name-compact">
              {host.nombre}
              {/* Indicador online/offline */}
              <FaCircle
                size={8}
                className="status-indicator"
                style={{
                  color: host.is_online ? '#10b981' : '#6b7280',
                  marginLeft: '8px'
                }}
                title={host.is_online ? 'Online' : 'Offline'}
              />
            </h4>
          </div>
          <div className="host-subtitle">
            {host.marca && host.modelo && <span>{host.marca} {host.modelo}</span>}
            {host.marca && host.modelo && (host.serial || host.ip) && <span className="separator">•</span>}
            {host.serial && <span className="host-serial-text">S/N: {host.serial}</span>}
            {host.serial && host.ip && <span className="separator">•</span>}
            {host.ip && <span className="host-ip-text">{host.ip}</span>}
          </div>
        </div>
        <button className="expand-btn-compact">
          {isExpanded ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
        </button>
      </div>

      {/* Métricas - Solo si hay datos */}
      {hasMetrics && (
        <div className="host-metrics-section">
          <div className="metrics-grid">
            <MetricBar label="CPU" value={host.cpu_usage} />
            <MetricBar label="RAM" value={host.ram_usage} />
            <MetricBar label="Disco" value={host.disk_usage} />
          </div>
        </div>
      )}

      {/* Specs en grid limpio */}
      <div className="host-specs-grid">
        {host.sistema_operativo && (
          <div className="spec-item">
            <span className="spec-label">Sistema Operativo</span>
            <span className="spec-value">{host.sistema_operativo}</span>
          </div>
        )}
        {host.cpu && (
          <div className="spec-item">
            <span className="spec-label">CPU</span>
            <span className="spec-value">{host.cpu}</span>
          </div>
        )}
        {host.ram && (
          <div className="spec-item">
            <span className="spec-label">RAM</span>
            <span className="spec-value">{host.ram}</span>
          </div>
        )}
        {host.almacenamiento && (
          <div className="spec-item">
            <span className="spec-label">Almacenamiento</span>
            <span className="spec-value">{host.almacenamiento}</span>
          </div>
        )}
      </div>

      {/* Footer simple */}
      <div className="host-footer-compact">
        <span className="vms-count-text">{host.vms?.length || 0} máquinas virtuales</span>
      </div>
    </div>
  );
};

const VMNode = ({ vm }) => {
  const OSIcon = getOSIcon(vm.sistema_operativo);
  const osColor = getOSColor(vm.sistema_operativo);
  const hasMetrics = vm.cpu_usage != null || vm.ram_usage != null || vm.disk_usage != null;

  return (
    <div className="node-vm">
      <div className="vm-header-row">
        <div className="vm-icon-wrapper" style={{ color: osColor }}>
          <OSIcon size={20} />
        </div>
        <div className="vm-main-content">
          <div className="vm-title-group">
            <h5 className="vm-title">
              {vm.nombre}
              {/* Indicador online/offline */}
              <FaCircle
                size={6}
                className="status-indicator"
                style={{
                  color: vm.is_online ? '#10b981' : '#6b7280',
                  marginLeft: '6px'
                }}
                title={vm.is_online ? 'Online' : 'Offline'}
              />
            </h5>
          </div>
          {vm.ip && <span className="vm-ip-address">{vm.ip}</span>}
        </div>
      </div>

      {vm.proposito && <p className="vm-purpose">{vm.proposito}</p>}
      {vm.descripcion && <p className="vm-description">{vm.descripcion}</p>}

      {/* Métricas - Solo si hay datos */}
      {hasMetrics && (
        <div className="vm-metrics-section">
          <div className="vm-metrics-compact">
            {vm.cpu_usage != null && <MetricBar label="CPU" value={vm.cpu_usage} />}
            {vm.ram_usage != null && <MetricBar label="RAM" value={vm.ram_usage} />}
            {vm.disk_usage != null && <MetricBar label="Disco" value={vm.disk_usage} />}
          </div>
        </div>
      )}

      <div className="vm-specs-list">
        {vm.sistema_operativo && (
          <div className="vm-spec-row">
            <span className="vm-spec-label">OS</span>
            <span className="vm-spec-value">{vm.sistema_operativo}</span>
          </div>
        )}
        {vm.vcpu && (
          <div className="vm-spec-row">
            <span className="vm-spec-label">CPU</span>
            <span className="vm-spec-value">{vm.vcpu} vCPU</span>
          </div>
        )}
        {vm.vram && (
          <div className="vm-spec-row">
            <span className="vm-spec-label">RAM</span>
            <span className="vm-spec-value">{vm.vram}</span>
          </div>
        )}
        {vm.almacenamiento && (
          <div className="vm-spec-row">
            <span className="vm-spec-label">Storage</span>
            <span className="vm-spec-value">{vm.almacenamiento}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export function ServersHierarchyView({ data = [], loading, error }) {
  const [selectedRack, setSelectedRack] = useState('all');
  const [expandedHosts, setExpandedHosts] = useState({});

  const filteredData = useMemo(() => {
    if (selectedRack === 'all') return data;
    return data.filter(rack => rack.id === parseInt(selectedRack));
  }, [data, selectedRack]);

  // Expandir todos los hosts por defecto
  React.useEffect(() => {
    if (data && data.length > 0) {
      const allHostIds = {};
      data.forEach(rack => {
        if (rack.hosts) {
          rack.hosts.forEach(host => {
            allHostIds[host.id] = true;
          });
        }
      });
      setExpandedHosts(allHostIds);
    }
  }, [data]);

  const toggleHost = (hostId) => {
    setExpandedHosts(prev => ({
      ...prev,
      [hostId]: !prev[hostId]
    }));
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
      <div className="w-12 h-12 rounded-full border-3 border-gray-200 border-t-gray-600 animate-spin" />
      <p className="mt-4 text-sm font-medium text-shimmer">Cargando infraestructura...</p>
    </div>
  );
  if (error) return <div className="error">{error}</div>;
  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <FaServer size={48} color="#ccc" />
        <p>No hay racks configurados</p>
        <small>Agrega un rack para comenzar a organizar tu infraestructura</small>
      </div>
    );
  }

  return (
    <>
      {/* Vista de Nodos Moderna */}
      <div className="modern-hierarchy-container">
        {filteredData.map((rack, rackIndex) => (
          <div key={rack.id} className="rack-section-modern animate-fadeIn" style={{ animationDelay: `${rackIndex * 0.15}s` }}>
            {/* Rack */}
            <RackNode rack={rack} />

            {/* Hosts */}
            {rack.hosts && rack.hosts.length > 0 ? (
              <div className="hosts-list">
                {rack.hosts.map((host, hostIndex) => (
                  <div key={host.id} className="host-item animate-slideUp" style={{ animationDelay: `${hostIndex * 0.08}s` }}>
                    <HostNode
                      host={host}
                      isExpanded={expandedHosts[host.id]}
                      onToggle={() => toggleHost(host.id)}
                    />

                    {/* VMs Grid - Solo si está expandido */}
                    {expandedHosts[host.id] && host.vms && host.vms.length > 0 && (
                      <div className="vms-grid">
                        {host.vms.map((vm, vmIndex) => (
                          <div key={vm.id} className="animate-scaleIn" style={{ animationDelay: `${vmIndex * 0.05}s` }}>
                            <VMNode vm={vm} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-message">No hay hosts configurados en este rack</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
